import React from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Column } from '../../components/DataTable/DataTable';
import Badge from '../../components/ui/Badge';
import { EyeIcon, PencilIcon, TrashIcon } from '../../components/ui/Icons';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import type { VesselOperationType } from './useData';

type Rank = { id: number; name: string; abrv?: string | null };

const RankIdsField: React.FC<{
  value: unknown;
  onChange: (next: number[]) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const { colors } = useTheme();
  const { data: lookups } = useSWR<{ ranks: Rank[] }>('/crew/lookups', fetcher, {
    revalidateOnFocus: false});
  const ranks = lookups?.ranks ?? [];
  const selected = Array.isArray(value) ? value.map(Number) : [];
  const isChecked = (id: number) => selected.includes(id);
  const toggle = (id: number) => {
    if (disabled) return;
    onChange(isChecked(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <div
      className={cn(
        'mt-1 grid max-h-56 grid-cols-1 gap-2 overflow-y-auto rounded-md border p-3 sm:grid-cols-2',
        colors.border,
        colors.surface,
        disabled && 'pointer-events-none opacity-50',
      )}
      role="group"
      aria-label="Assigned ranks"
    >
      {ranks.length === 0 ? (
        <span className={cn('text-sm', colors.textMuted)}>No ranks available</span>
      ) : (
        ranks.map((r) => {
          const id = `rank-${r.id}`;
          return (
            <label
              key={r.id}
              htmlFor={id}
              className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 hover:bg-black/5 dark:hover:bg-white/5"
            >
              <input
                id={id}
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={isChecked(r.id)}
                onChange={() => toggle(r.id)}
                disabled={disabled}
              />
              <span className={cn('text-sm leading-tight', colors.text)}>
                {r.abrv ? `${r.abrv} — ${r.name}` : r.name}
              </span>
            </label>
          );
        })
      )}
    </div>
  );
};

function sanitizePayload(data: Partial<VesselOperationType>) {
  return {
    name: String(data.name ?? '').trim(),
    color: String(data.color ?? '#888888').trim() || '#888888',
    notes: data.notes?.toString().trim() || null,
    sortOrder: Number(data.sortOrder) || 0,
    isActive: data.isActive !== false,
    rankIds: Array.isArray(data.rankIds) ? data.rankIds.map(Number).filter(Number.isFinite) : []};
}

export const moduleConfig = {
  module: {
    title: 'Vessel Operation Types',
    permissionModuleKey: 'wrh',
    route: {
      list: '/wrh/operation-types',
      new: '/wrh/operation-types/new',
      detail: (id: number) => `/wrh/operation-types/${id}`,
      edit: (id: number) => `/wrh/operation-types/${id}/edit`},
    api: {
      list: '/vessel-operation-types',
      detail: (id: number) => `/vessel-operation-types/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by description or notes…' },
  filterGroups: [{ name: 'basic', label: 'Basic', icon: '', fields: ['name', 'isActive'] }],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Description',
        placeholder: 'All',
        getValue: (item: VesselOperationType) => item.name,
        options: []},
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: VesselOperationType) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: VesselOperationType[], filters: Record<string, string[]>) =>
      items.filter((item) => {
        if (filters.name?.length) {
          const v = item.name || '';
          if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      })},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New',
        showTitle: false,
        variant: 'primary' as const,
        href: '/wrh/operation-types/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: VesselOperationType) => item.id,
  },
  emptyStates: {
    noData: 'No operation types yet. Add types for the vessel operations schedule.',
    noSearchResults: 'No operation types match your search.',
    noFilterResults: 'No operation types match the filters.',
  },
  delete: {
    title: 'Delete operation type',
    message: 'Delete this operation type? Types in use are deactivated instead.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete operation type.',
    handler: async (id: number) => {
      const { deleteVesselOperationType } = await import('../../services/mutations');
      await deleteVesselOperationType(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This operation type does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Operation type',
    getTitle: (item: VesselOperationType) => item.name || `Operation type #${item.id}`},
  form: {
    new: {
      title: 'New operation type',
      subtitle: 'Color and ranks for vessel operations / WRH overlays',
      submitText: 'Create'},
    edit: { title: 'Edit operation type', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<VesselOperationType>) => {
      const { createVesselOperationType } = await import('../../services/mutations');
      return createVesselOperationType(sanitizePayload(data));
    },
    update: async (id: number, data: Partial<VesselOperationType>) => {
      const { updateVesselOperationType, setVesselOperationTypeRanks } = await import(
        '../../services/mutations'
      );
      const payload = sanitizePayload(data);
      const { rankIds, ...rest } = payload;
      await updateVesselOperationType(id, rest);
      await setVesselOperationTypeRanks(id, rankIds);
      return { id, ...payload };
    }},
  fields: [
    {
      name: 'name',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'e.g. Mooring, Cargo ops',
      validation: { required: true, minLength: 1, maxLength: 120 }},
    {
      name: 'color',
      label: 'Color',
      type: 'color',
      required: true,
      validation: { required: true }},
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Optional notes',
      validation: { maxLength: 500 }},
    {
      name: 'sortOrder',
      label: 'Sort order (AA)',
      type: 'number',
      defaultValue: 0},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      defaultValue: true},
    {
      name: 'rankIds',
      label: 'Assigned ranks',
      type: 'custom',
      helperText: 'Ranks that typically perform this operation',
      customRender: ({ value, onChange }) => (
        <RankIdsField value={value} onChange={(next) => onChange(next)} />
      )},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Operation type',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: ['name', 'color', 'notes', 'sortOrder', 'isActive', 'rankIds'],
        fieldSpans: {
          name: 'md:col-span-8',
          color: 'md:col-span-4',
          notes: 'md:col-span-12',
          sortOrder: 'md:col-span-3',
          isActive: 'md:col-span-9',
          rankIds: 'md:col-span-12'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<VesselOperationType>[] => [
  { key: 'name', header: 'Description', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'color',
    header: 'Color',
    sortable: false,
    render: (_v, row) => (
      <span
        className="inline-block h-5 w-10 rounded border border-black/10"
        style={{ backgroundColor: row?.color }}
      />
    )},
  { key: 'sortOrder', header: 'AA', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 rounded-md border px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: VesselOperationType, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Description', value: item.name },
      {
        label: 'Color',
        value: (
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block h-5 w-10 rounded border border-black/10"
              style={{ backgroundColor: item.color }}
            />
            <span>{item.color}</span>
          </span>
        )},
      { label: 'Notes', value: item.notes || '—' },
      { label: 'Sort order', value: String(item.sortOrder) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      {
        label: 'Assigned ranks',
        value:
          (item.ranks ?? []).map((r) => (r.abrv ? `${r.abrv} — ${r.name}` : r.name)).join(', ') ||
          '—'},
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<VesselOperationType> => ({
  name: '',
  color: '#3A86FF',
  notes: '',
  sortOrder: 0,
  isActive: true,
  rankIds: []});

export const mapItemToFormData = (item: VesselOperationType): Partial<VesselOperationType> => ({
  name: item.name ?? '',
  color: item.color ?? '#888888',
  notes: item.notes ?? '',
  sortOrder: item.sortOrder ?? 0,
  isActive: item.isActive ?? true,
  rankIds: item.rankIds ?? item.ranks?.map((r) => r.id) ?? []});

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

const TableActionsCell: React.FC<{
  item: VesselOperationType;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex flex-nowrap items-center">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <EyeIcon className="h-5 w-5" />
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <PencilIcon className="h-5 w-5" />
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
        title="Delete"
      >
        <TrashIcon className="h-5 w-5" />
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: VesselOperationType,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell
    item={item}
    onDelete={onDelete}
    canDelete={options?.canDelete !== false}
    canEdit={options?.canEdit !== false}
  />
);

export type ModuleConfig = typeof moduleConfig;
